/* 取引店 */
import React, { memo, useEffect, useState } from 'react';
import { FormControl, Button } from 'react-bootstrap';
import RowTemplate from '../atoms/RowTemplate';
import Messages from '../molecules/Messages';
import SelectOffice from './SelectOffice';
import axios from 'axios';
import Paragraph from './Paragraph';
import LoadingModal from './LoadingModal';
import { authErrorTimeout } from '../Common';
import { http_status_code_list, network_error_message, office_type, fm_types } from '../Const';

interface Props {
  id: string;
  fm_id?: number;
  label: string;
  need?: boolean;
  value: string;
  placeholder: string;
  handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  message?: string;
  name: string;
  help_msg?: string;
  disabled: boolean;
  maxlength?: number;
  handleBlur?: VoidFunction;
  handleSelectDealer: (dealer: string, offce_id: string, name: string) => void;
  jacode?: String; // jacodeのvalueを受け取る
  looks_inactive: boolean;
  readonly?: boolean;
  office_cd: string;
  is_bank_exists?: boolean; // 取引店が存在してるかのフラグ
  is_ja_exists?: boolean; // JAコードに紐づくJAが存在するかのフラグ
  is_mypage_ja?: boolean; // マイページのJAを使用しているかのフラグ
  fm_type: string;
}

interface OfficeInfo {
  id: string;
  name: string;
}

const Office: React.FC<Props> = ({
  id,
  fm_id,
  label,
  need,
  value,
  placeholder,
  handleChange,
  message,
  name,
  help_msg,
  disabled,
  maxlength,
  handleBlur,
  handleSelectDealer,
  jacode,
  looks_inactive,
  office_cd,
  is_bank_exists,
  is_ja_exists,
  is_mypage_ja,
  fm_type,
}) => {
  const [modal_open, setModalOpen] = useState(false);
  const [office_list, setOfficeList] = useState([]);
  const [error_message, setErrorMessage] = useState('');
  const [is_loading, setIsLoading] = useState(false);

  useEffect(() => {
    // 初期読み込み

    // note 参照画面の場合は、案件に保存されている取引店コード・取引店を表示
    if (disabled || looks_inactive ) {
      return;
    }
    init();
  }, []);

  // 支店リストの取得
  const init = async () => {
    // マイページのJAを使用している場合、支店リストの取得を行わない
    if(is_mypage_ja) return;

    try {
      // エラーメッセージをリセットする
      setErrorMessage('');
      setIsLoading(true);

      // お取引希望店舗かどうかの判定
      const type =
        fm_type === fm_types.desired_trading_office
          ? office_type.desired_trading
          : office_type.account_infomation;
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/office_search`,
        {
          jacode: jacode,
          type: type,
        }
      );
      // 取得した情報を取引店一覧に表示
      setOfficeList(response.data.detail);

      if (response.data.result) {
        // エラーメッセージをリセットする
        setErrorMessage('');
        response.data.detail.map((office: OfficeInfo) => {
          if (office.id === office_cd) {
            handleSelectDealer(office.id, office.name, name);
          }
        });
      }
    } catch (error) {
      if (error.message === network_error_message) {
        // ネットワークエラーなので認証エラーとしてタイムアウト画面に飛ばす
        authErrorTimeout(http_status_code_list.unauthorized);
      }
      handleSelectDealer('', '', name);
      setOfficeList([]);
      setErrorMessage(error.response.data.message);
    } finally {
      setIsLoading(false);
    }
  };

  // モーダルを開く
  const handleClickSelect = () => {
    setModalOpen(true);
    init();
  };

  // モーダルを閉じる
  const handleClickModalClose = () => {
    setModalOpen(false);
  };

  /**
   * 通常の取引店を描画する
   * @return JSX.Element JAコードのElement
   */
  const returnOfficeJsx = () => {
    return (
      <>
        <div className="text-btn_area">
          <FormControl
            className={message && 'textarea-error'}
            id={id}
            data-fm_id={fm_id}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            name={name}
            disabled={true}
            onBlur={handleBlur}
            maxLength={maxlength}
          />
          <Button
            size="sm"
            variant="success"
            className="btn-form"
            onClick={handleClickSelect}
            disabled={is_bank_exists && is_ja_exists}
          >
            選択
          </Button>
        </div>
        <div>
          <Messages help_msg={is_bank_exists && is_ja_exists ? '' : help_msg} valid_msg={message} />
          <SelectOffice
            toggleShow={modal_open}
            toggleClose={handleClickModalClose}
            popupClick={(office_info: OfficeInfo) => {
              handleSelectDealer(office_info.id, office_info.name, name);
              handleClickModalClose();
            }}
            office_list={office_list}
            message={error_message}
          />
        </div>
        <LoadingModal show={is_loading} />
      </>
    );
  };

  /**
   * ラベルを描画する
   * @return JSX.Element ラベルのElement
   */
  const returnLabelJsx = () => {
    return <>{<Paragraph label={value} />}</>;
  };

  return (
    <RowTemplate
      label={label}
      need={need}
      disabled={disabled}
      looks_inactive={looks_inactive}
      content={
        <div className="dealer-content">
          {disabled || looks_inactive ? returnLabelJsx() : returnOfficeJsx()}
        </div>
      }
    />
  );
};

export default memo(Office);
